import type { BluffMove, BluffView } from "@bing/engine-bluff";
import { useCardLandSfx, useDealSfx, useEventSfx } from "../game/use-table-sfx";
import { useEffect, useState, type ReactNode } from "react";
import { Avatar } from "../components/Avatar";
import { Card } from "../components/Card";
import { GameIcon } from "../components/GameIcon";
import { rowSeatEdge, ThrownCard } from "../game/card-motion";
import { fa } from "../i18n/fa";
import { playSfx } from "../sound/sfx";
import {
  resolveSeatAvatar,
  resolveSeatName,
  type SeatAvatar,
  type SeatIdentity,
  type SeatPlayer,
} from "../game/seat-identity";
import { bluffSeatFallback } from "../game/seat-labels";
import {
  noticeForSeat,
  SEAT_NOTICE_DIM,
  SeatNoticeText,
  type SeatNotice,
} from "../game/seat-notice";

/* ------------------------------------------------------------------ *
 * Bluff (بلوف / Cheat) game table — a faithful Tailwind port of the
 * Stitch card-room frame (docs/stitch/08-bluff-table.html): a radial-felt
 * table with a minimal top bar, an opponents row (avatar + active gold
 * glow + name/count chip), a central HUD (poker-chip "claim of rank"
 * circle, the face-down PILE, and a "last hand" bluff/truth badge), and
 * the local player's fanned, selectable hand under the action row. Card
 * faces are NEVER shown for any seat but your own — the whole
 * hidden-information point of the game.
 *
 * Controls are derived from the authoritative `legal` list the server
 * sends alongside the redacted view:
 *   • a `{type:"play"}` list  → your turn to play: pick 1–N cards and
 *     lay them down claiming the fixed `currentRank`.
 *   • a `{type:"call"}` / `{type:"pass"}` pair → the challenge window:
 *     call the last play a bluff, or pass.
 * All props, handlers and the Card/tap sound wiring are preserved — only
 * presentation moves to Tailwind (Stitch tokens).
 * ------------------------------------------------------------------ */

type PlayMove = Extract<BluffMove, { readonly type: "play" }>;

const AVATAR_IDS = ["lion", "wolf", "tiger", "eagle", "fox", "owl"] as const;
function avatarFor(seat: number): string {
  return AVATAR_IDS[seat % AVATAR_IDS.length] ?? "fox";
}

/** Persian-first UI: render dynamic numbers with Persian digits. */
const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
function faNum(n: number): string {
  return String(n).replace(/\d/g, (d) => FA_DIGITS[Number(d)] ?? d);
}

// The anonymous-seat label is the ABSOLUTE seat, already viewer-INDEPENDENT (seat 2 is «حریف ۲»
// everywhere). Only the games that numbered RELATIVE to the viewer (منچ/بی‌دل/حکم/شلم) had the
// "same bot, different number for two players" bug. It is now only a FALLBACK — a resolved human
// shows their real name.

/** Human-facing rank label for a numeric Rank (Ace = 14). Local so the web app
 * needn't depend on `@bing/engine-core` just for this one helper. */
function rankText(rank: number): string {
  switch (rank) {
    case 14:
      return "A";
    case 13:
      return "K";
    case 12:
      return "Q";
    case 11:
      return "J";
    default:
      return faNum(rank);
  }
}

/* ---- Inline Material-Symbol SVGs (the design used the icon font we don't ship) ---- */

/** Material "front_hand" (raised palm) — the call-bluff CTA. */
function FrontHandIcon({ size = 20 }: { size?: number }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path d="M23 5.5V20c0 2.2-1.8 4-4 4h-7.3c-1.08 0-2.1-.43-2.85-1.19L1 14.83s1.26-1.23 1.3-1.25c.22-.19.49-.29.79-.29.22 0 .42.06.6.16.04.01 4.31 2.46 4.31 2.46V4c0-.83.67-1.5 1.5-1.5S12 3.17 12 4v7h1V1.5c0-.83.67-1.5 1.5-1.5S16 .67 16 1.5V11h1V2.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V11h1V5.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5z" />
    </svg>
  );
}

/** Material "style"/"playing_cards" — the lay-down CTA. */
function CardsIcon({ size = 16 }: { size?: number }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path
        d="M8 5h11a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z"
        opacity=".55"
      />
      <path d="M4 8v10a2 2 0 0 0 2 2h9v-2H6V8H4z" />
    </svg>
  );
}

/** Material "close" — the ✗ on a caught-bluff result badge. */
function CloseIcon({ size = 14 }: { size?: number }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

/** One opponent seat: avatar (+ active gold glow) and a name/count chip. Only the
 * hand *count* is ever shown — never card identities. */
function Seat({
  name,
  avatar,
  count,
  active,
  notice = null,
}: {
  /** Already resolved by the table — a real player's name, else «حریف N». */
  name: string;
  /** Already resolved by the table — their photo/preset, else the positional preset. */
  avatar: SeatAvatar;
  count: number;
  active: boolean;
  /** What has just happened to this seat, said on it — see `seat-notice.tsx`. */
  notice?: SeatNotice | null;
}): ReactNode {
  const countLabel = `${faNum(count)} کارت`;
  return (
    <div className="flex flex-col items-center gap-1.5 min-w-0">
      <div className="relative">
        <div
          className="rounded-full"
          style={{
            padding: 3,
            boxShadow: active
              ? "0 0 15px 4px rgb(var(--c-secondary) / .35), 0 0 0 2px rgb(var(--c-secondary))"
              : "0 0 0 2px rgb(var(--c-outline-variant)), 0 4px 6px rgba(0,0,0,.4)",
            animation: active ? "turn-glow-pulse 1.6s ease-in-out infinite" : undefined,
            opacity: notice?.dim === true ? SEAT_NOTICE_DIM : undefined,
          }}
        >
          <Avatar
            avatarId={avatar.avatarId}
            avatarPhotoId={avatar.avatarPhotoId}
            size={44}
            official={avatar.official}
          />
        </div>
        {/* ⚠️ ON بلوف THE COUNT MOVES, IT IS NOT DROPPED. The chip below holds the name AND «N کارت»
            in 96 px, which leaves the name ~39 px — «در حال اتصال» (64 px) would be cut. So while
            the seat reconnects the chip carries the status alone and the count rides the avatar,
            outside the dimmed ring so it stays legible: a hand count is play information. */}
        {notice ? (
          <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-outline-variant bg-surface-container-high px-1.5 py-0.5 text-caption text-secondary shadow-md">
            {countLabel}
          </span>
        ) : null}
      </div>
      <div
        className={`flex items-center gap-1 px-2 py-0.5 rounded-full border max-w-[96px] ${
          active
            ? "bg-secondary-container border-secondary"
            : "bg-surface-container-high border-outline-variant"
        }`}
      >
        <span
          className={`text-caption truncate ${
            active ? "text-on-secondary-container font-bold" : "text-on-surface"
          }`}
        >
          {notice ? <SeatNoticeText notice={notice} /> : name}
        </span>
        {notice ? null : (
          <span className="text-caption text-secondary whitespace-nowrap">{countLabel}</span>
        )}
      </div>
    </div>
  );
}

/** The central poker-chip "claim of rank" HUD — the design's `bluffPile` chip.
 * Shows the standing claim backing the pile (count × rank), or the rank in play
 * before the first claim of a round. Never a card face. */
function ClaimChip({ view }: { view: BluffView }): ReactNode {
  const lastClaim = view.pileClaims[view.pileClaims.length - 1] ?? null;
  return (
    <div
      className="relative w-24 h-24 rounded-full bg-surface border-4 border-secondary flex flex-col items-center justify-center"
      style={{ boxShadow: "0 8px 16px rgba(0,0,0,.8)" }}
    >
      <span
        className="absolute inset-1.5 rounded-full border-2 border-dashed border-felt-edge pointer-events-none"
        aria-hidden
      />
      <span className="text-[10px] leading-none text-on-surface-variant mb-1">
        {fa.bluff.claimRank}
      </span>
      {lastClaim ? (
        <span className="flex items-baseline gap-1">
          <span className="text-[22px] font-extrabold leading-none text-danger-bluff">
            {faNum(lastClaim.count)}
          </span>
          <span className="text-[13px] text-on-surface">تا</span>
          <span className="text-[22px] font-extrabold leading-none text-secondary">
            {rankText(lastClaim.claimedRank)}
          </span>
        </span>
      ) : (
        <span className="text-[22px] font-extrabold leading-none text-secondary">
          {rankText(view.currentRank)}
        </span>
      )}
    </div>
  );
}

/** The central face-down PILE: card backs (identities never leave the server) and
 * a public total-count caption. */
function Pile({ view }: { view: BluffView }): ReactNode {
  const shown = Math.min(view.pileCount, 5);
  // The just-played cards = the last claim's `count` (the only currently
  // challengeable play). Of the fanned representative backs, the TOP `newCount`
  // are those fresh arrivals and fly in. `pileClaims.length` is a per-play
  // "generation" (grows by one each play, resets when the pile clears), so keying
  // ONLY the new backs by it re-mounts (→ re-runs the flight on) just the arrivals
  // each play, while settled backs keep a stable key and never re-animate.
  const lastClaim = view.pileClaims[view.pileClaims.length - 1];
  const newCount = Math.min(lastClaim?.count ?? 0, shown);
  const gen = view.pileClaims.length;
  // …and they fly in from the CLAIMER's own seat — yours up out of your hand, an
  // opponent's down from the top row where every opponent is pinned. The claim
  // record is the only public link from a face-down back to who put it there.
  const claimEdge = lastClaim ? rowSeatEdge(lastClaim.player, view.you) : "bottom";
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        aria-label="pile"
        className="relative"
        style={{ width: 46 + Math.max(0, shown - 1) * 4, height: 64 }}
      >
        {shown === 0 ? (
          <span
            className="absolute inset-0 w-11 h-16 rounded-md border-2 border-dashed border-white/25"
            aria-hidden
          />
        ) : (
          Array.from({ length: shown }).map((_u, i) => {
            const isNew = i >= shown - newCount;
            return (
              <span
                key={isNew ? `throw-${gen}-${i}` : `back-${i}`}
                className="absolute"
                style={{
                  left: i * 4,
                  top: i * -1.5,
                  transformOrigin: "bottom center",
                  transform: `rotate(${((i - (shown - 1) / 2) * 4).toFixed(1)}deg)`,
                }}
              >
                {/* The flight rides an inner wrapper so it doesn't fight the fan
                    rotate above; it settles to transform none, leaving the wrapper's
                    fan angle intact. Only fresh arrivals carry it (settled backs pass
                    `edge={null}` = placed, no motion). It used to be the bottom-only
                    `card-throw` keyframe, so an opponent's cards flew UP into the pile
                    from the viewer's own side of the table. */}
                <ThrownCard edge={isNew ? claimEdge : null}>
                  <Card id="" faceDown />
                </ThrownCard>
              </span>
            );
          })
        )}
      </div>
      {view.pileCount > 0 && (
        <span className="px-2.5 py-0.5 rounded-full bg-surface-container-low/80 border border-outline-variant/60 text-caption text-on-surface-variant whitespace-nowrap">
          {`${fa.bluff.pile} · ${faNum(view.pileCount)}`}
        </span>
      )}
    </div>
  );
}

/** The design's "Previous Action HUD" — a green/red pill summarising whether the
 * last resolved play was a bluff. Carries an outcome only, never card faces. */
function LastResult({ res }: { res: BluffView["lastResolution"] }): ReactNode {
  if (!res) return null;
  const bluff = res.wasBluff;
  return (
    <div
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-sm border whitespace-nowrap bg-surface-container/90 ${
        bluff
          ? "border-danger-bluff/40 text-danger-bluff"
          : "border-success-win/40 text-success-win"
      }`}
      style={{ boxShadow: "0 4px 10px rgba(0,0,0,.4)" }}
    >
      <span className="text-caption text-on-surface-variant">دست قبل:</span>
      <span className="text-caption font-bold">{bluff ? "بلوف بود" : "راست بود"}</span>
      {bluff ? <CloseIcon /> : <GameIcon type="check" size={14} />}
    </div>
  );
}

export function BluffTable({
  view,
  legal,
  onMove,
  onLeave,
  self,
  players,
  seatNotices,
}: {
  view: BluffView;
  legal: readonly BluffMove[];
  onMove: (move: BluffMove) => void;
  onLeave: () => void;
  /** The signed-in user — renders YOUR seat with your real photo + name. */
  self?: SeatIdentity | null;
  /** The lobby roster: real occupants get their own name, face and verified badge; a bot (or a
   *  human the server could not resolve) keeps the positional stand-in. */
  players?: readonly SeatPlayer[];
  /** Online: what has just happened to each seat, said on that seat (`seat-notice.tsx`). */
  seatNotices?: readonly SeatNotice[] | undefined;
}): ReactNode {
  const you = view.you;
  // Names and faces come from ONE place, so a seat reads the same in the chip and the turn label.
  const nameOf = (seat: number): string =>
    resolveSeatName(seat, you, self, players, {
      you: fa.bluff.you,
      fallback: bluffSeatFallback,
    });
  const avatarOf = (seat: number): SeatAvatar =>
    resolveSeatAvatar(seat, you, self, players, avatarFor);

  // Table sounds: the ONE deal shuffles on entry (never on a resume, and never
  // when you pick the pile up — `onGrowth:false`, a pickup is not a deal); each
  // claim another seat slides onto the pile clicks; and a resolved CHALLENGE —
  // the moment the whole game turns on, previously silent — sounds for both sides.
  const claims = view.pileClaims;
  const lastClaimRec = claims[claims.length - 1];
  const resolution = view.lastResolution;
  useDealSfx(view.hand.length, claims.length === 0 && resolution === null, { onGrowth: false });
  useCardLandSfx(
    lastClaimRec
      ? {
          seat: lastClaimRec.player,
          key: `${claims.length}:${lastClaimRec.player}:${lastClaimRec.claimedRank}:${lastClaimRec.count}`,
        }
      : null,
    you,
  );
  useEventSfx(
    resolution ? `${resolution.caller}:${resolution.taker}:${String(resolution.wasBluff)}` : "",
    resolution && resolution.taker === you ? "trickLose" : "trickWin",
  );
  const opponents = view.handCounts.map((_c, seat) => seat).filter((seat) => seat !== you);

  // Derive the controls from the authoritative legal list.
  const playMoves = legal.filter((m): m is PlayMove => m.type === "play");
  const canPlay = playMoves.length > 0;
  const canCall = legal.some((m) => m.type === "call");
  const canPass = legal.some((m) => m.type === "pass");

  // All legal plays share the current claimed rank; the card-counts they allow
  // drive selection (1‥min(4, hand)). Only those counts enable the play button.
  const legalCounts = new Set(playMoves.map((m) => m.cards.length));
  const maxCount = playMoves.reduce((mx, m) => Math.max(mx, m.cards.length), 0);
  const claimedRank = playMoves[0]?.claimedRank ?? view.currentRank;

  const [selected, setSelected] = useState<string[]>([]);
  // Reset the selection whenever the decision context changes (new turn / phase /
  // a play landed on the pile / the rank advanced).
  const ctxKey = `${view.phase}|${view.turn}|${view.challenger}|${view.pileCount}|${view.currentRank}`;
  useEffect(() => {
    setSelected([]);
  }, [ctxKey]);

  const toggle = (card: string): void =>
    setSelected((cur) => (cur.includes(card) ? cur.filter((c) => c !== card) : [...cur, card]));

  const submitPlay = (): void => {
    if (!legalCounts.has(selected.length)) return;
    playSfx("tap");
    onMove({ type: "play", cards: selected, claimedRank });
  };

  const activeSeat =
    view.phase === "play" ? view.turn : view.phase === "challenge" ? view.challenger : -1;
  const turnLabel =
    activeSeat === you
      ? fa.bluff.yourTurn
      : activeSeat >= 0
        ? `نوبت ${nameOf(activeSeat)}`
        : "راست بگو یا بلوف بزن";

  const playEnabled = legalCounts.has(selected.length);

  // Fanned hand geometry — ported from the design's `buildFan`, fitted to a phone.
  const hand = view.hand;
  const n = hand.length;
  const w = 44;
  const step = n > 1 ? Math.min(w * 0.58, (300 - 1.25 * w) / (n - 1)) : 0;
  const fanWidth = (n - 1) * step + w;
  const half = (n - 1) / 2;
  const handHeight = Math.round(w * 1.42 + half * half * 1.1 + 32);

  return (
    <div
      className="relative w-full mx-auto max-w-[640px] flex flex-col overflow-hidden text-on-surface"
      style={{
        height: "var(--game-h)",
        background:
          "radial-gradient(circle at center,rgb(var(--c-primary-container)) 0%,rgb(var(--c-felt-deep)) 60%,rgb(var(--c-felt-edge)) 100%)",
        boxShadow: "inset 0 8px 32px rgb(var(--c-felt-shadow) / .7)",
      }}
    >
      {/* Top bar: exit · title/turn · balance spacer */}
      <header
        className="absolute top-0 inset-x-0 z-50 flex justify-between items-center px-4 pb-2 bg-gradient-to-b from-surface-container-highest/80 to-transparent"
        style={{
          paddingTop: "calc(10px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))",
        }}
      >
        <button
          type="button"
          aria-label={fa.bluff.leave}
          onClick={onLeave}
          className="p-2 text-secondary rounded-full hover:bg-surface-bright/20 active:scale-95 transition-transform"
        >
          <GameIcon type="exit" size={22} />
        </button>

        <div className="flex flex-col items-center leading-tight min-w-0 px-2">
          <h1 className="text-headline-sm text-secondary font-bold">{fa.appName}</h1>
          <span className="text-caption text-on-surface-variant truncate max-w-[220px]">
            {`${fa.bluff.title} · ${turnLabel}`}
          </span>
        </div>

        <span className="w-9" />
      </header>

      {/* Opponents row */}
      <div
        className="flex justify-around items-start gap-2 px-3 flex-none"
        style={{
          paddingTop: "calc(64px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))",
        }}
      >
        {opponents.map((seat) => (
          <Seat
            key={seat}
            name={nameOf(seat)}
            avatar={avatarOf(seat)}
            count={view.handCounts[seat] ?? 0}
            active={seat === activeSeat}
            notice={noticeForSeat(seatNotices, seat)}
          />
        ))}
      </div>

      {/* Felt center: claim chip · pile · last-result */}
      <div className="relative flex-1 min-h-0 grid place-items-center px-4">
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-60 h-52 rounded-full border border-felt-edge/40"
          style={{
            background: "radial-gradient(circle,rgb(var(--c-secondary) / .07),transparent 70%)",
          }}
          aria-hidden
        />
        <div className="relative z-10 flex flex-col items-center gap-3">
          <ClaimChip view={view} />
          <Pile view={view} />
          <LastResult res={view.lastResolution} />
        </div>
      </div>

      {/* Player area: fanned hand + action row */}
      <div
        className="relative z-30 flex-none w-full bg-gradient-to-t from-surface-container-lowest via-surface-container-lowest/80 to-transparent"
        style={{
          paddingBottom: "calc(12px + var(--game-safe-bottom))",
        }}
      >
        {/* Your hand (selectable during your play turn) */}
        <div
          aria-label="your hand"
          className="w-full flex justify-center px-2 pt-8 overflow-visible"
        >
          <div
            className="relative"
            style={{ height: handHeight, width: fanWidth, maxWidth: "100%" }}
          >
            {hand.map((card, i) => {
              const off = i - half;
              const isSel = selected.includes(card);
              const disabled = !canPlay || (!isSel && selected.length >= maxCount);
              const rise = off * off * 1.1 + (isSel ? 24 : 0);
              return (
                <div
                  key={card}
                  className="absolute"
                  style={{
                    bottom: 6,
                    left: Math.round(i * step),
                    transformOrigin: "bottom center",
                    transform: `translateY(${(-rise).toFixed(1)}px) rotate(${(off * 2.6).toFixed(1)}deg)`,
                    zIndex: isSel ? 100 + i : i,
                    transition: "transform .2s ease",
                  }}
                >
                  <Card
                    id={card}
                    dealIndex={i}
                    selected={isSel}
                    disabled={disabled}
                    onClick={() => toggle(card)}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Action row: play/claim · call/pass challenge · or a waiting hint */}
        <div className="px-4 pt-2 flex justify-center gap-3">
          {canPlay ? (
            <button
              type="button"
              disabled={!playEnabled}
              onClick={submitPlay}
              className="w-full max-w-[340px] h-14 rounded-xl flex items-center justify-center gap-2 bg-secondary text-ink-on-gold border border-gold-deep transition-all enabled:hover:bg-gold-deep enabled:active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                boxShadow: "0 10px 15px -3px rgba(0,0,0,.6), 0 4px 6px -2px rgba(0,0,0,.4)",
              }}
            >
              <CardsIcon size={18} />
              <span className="text-headline-sm">{fa.bluff.play}</span>
              <span aria-hidden className="text-caption font-bold opacity-80">
                {`${faNum(selected.length)} × ${rankText(claimedRank)}`}
              </span>
            </button>
          ) : canCall || canPass ? (
            <>
              <button
                type="button"
                disabled={!canCall}
                onClick={() => {
                  playSfx("tap");
                  onMove({ type: "call" });
                }}
                className="flex-1 max-w-[180px] h-14 rounded-xl flex items-center justify-center gap-2 bg-danger-bluff text-white border border-red-400/30 transition-all enabled:hover:bg-red-600 enabled:active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  boxShadow: "0 10px 15px -3px rgba(0,0,0,.6), 0 4px 6px -2px rgba(0,0,0,.4)",
                }}
              >
                <span className="text-body-lg">{fa.bluff.call}</span>
                <FrontHandIcon size={20} />
              </button>
              <button
                type="button"
                disabled={!canPass}
                onClick={() => {
                  playSfx("tap");
                  onMove({ type: "pass" });
                }}
                className="flex-1 max-w-[180px] h-14 rounded-xl flex items-center justify-center gap-2 bg-secondary text-ink-on-gold border border-gold-deep transition-all enabled:hover:bg-gold-deep enabled:active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  boxShadow: "0 10px 15px -3px rgba(0,0,0,.6), 0 4px 6px -2px rgba(0,0,0,.4)",
                }}
              >
                <span className="text-body-lg">{fa.bluff.pass}</span>
                <GameIcon type="skip" size={18} />
              </button>
            </>
          ) : (
            <span className="inline-flex items-center px-4 py-2 rounded-full text-body-md font-bold bg-surface-container-high/80 border border-outline-variant text-on-surface-variant">
              {fa.bluff.waiting}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
